import { Accordion, AccordionContent, AccordionPrimitive, AccordionTrigger, Icon, Loading } from '@anytag/shared-atoms';
import { cn } from '@anytag/shared-utils';
import { VideoItem, Rule, TypedVideoAnalyzerRuleBreakdown, RuleType } from '../../types';
import { AnalysisScoreChart } from './analysis-score-chart';

interface AnalysisIndividualViewProps {
  activeItem: VideoItem;
  rules: Rule[];
}

export const AnalysisIndividualView = ({ activeItem, rules }: AnalysisIndividualViewProps) => {
  const result = activeItem?.result;
  const getInfluencerName = (fileName: string) => (fileName ? fileName.replace(/\.mp4$/i, '') : 'Unknown Video');

  if (activeItem.status === 'PROCESSING') {
    return (
      <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
        <Loading>
          <p className="mt-4 font-medium text-tertiary">Analyzing {getInfluencerName(activeItem.fileName)}...</p>
        </Loading>
      </div>
    );
  }

  if (activeItem.status === 'ERROR') {
    return (
      <div className="p-8 text-center">
        <div className="mb-4 inline-flex rounded-full bg-attention-red/10 p-4 text-attention-red">
          <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-bold text-primary">Analysis Failed</h3>
        <p className="text-attention-red">Could not process this video. Please try again.</p>
      </div>
    );
  }

  if (activeItem.status === 'IDLE') {
    return (
      <div className="my-auto h-full text-center text-secondary">
        <p>Waiting to start analysis...</p>
      </div>
    );
  }

  if (activeItem.status === 'DONE' && result) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col items-center justify-between rounded-2xl border-base-border bg-black-10 p-4 md:flex-row">
          <div className="mb-4 flex items-center gap-6 md:mb-0">
            <AnalysisScoreChart result={result} />
            <div>
              <h2 className="mb-1 text-xl font-bold text-primary">Compliance Score</h2>
              <div className="flex gap-4 text-xs">
                <span className="text-green flex items-center gap-1">
                  <span className="size-2 rounded-full bg-attention-green"></span>
                  Passed
                </span>
                <span className="flex items-center gap-1 text-attention-red">
                  <span className="size-2 rounded-full bg-attention-red"></span>
                  Failed
                </span>
              </div>
            </div>
          </div>

          {result.suggestions && (
            <div className="flex-1 rounded-lg border-base-orange-50 bg-orange-10/30 p-3 md:ml-6">
              <h4 className="mb-1 text-xs font-bold text-orange-100 uppercase">Editor Feedback</h4>
              <p className="text-sm leading-snug text-orange-80">{result.suggestions}</p>
            </div>
          )}
        </div>

        {(() => {
          const detailedResults = result.ruleBreakdown.map((rResult) => {
            const originalRule = rules.find((rule) => rule.id === rResult.ruleId);
            return {
              ...rResult,
              ruleText: originalRule?.text || 'Unknown Rule',
              ruleType: originalRule?.type
            };
          });
          const doRules = detailedResults.filter((r) => r.ruleType === 'SHOULD');
          const dontRules = detailedResults.filter((r) => r.ruleType === 'SHOULD_NOT');

          return (
            <>
              <Accordion type="single" collapsible>
                <AccordionPrimitive.AccordionItem
                  value="do"
                  className={cn(
                    'rounded-sm border-base-border',
                    'not-data-[state=open]:hover:border-base-border-hover'
                  )}
                >
                  <AccordionTrigger hasIcon={false}>
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-bold tracking-wide text-attention-green uppercase">
                        <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Must Include (Do)
                      </div>
                      <Icon icon="caret-down" size={9} variant="black" className="group-data-[state=open]:rotate-180" />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-2">
                    {doRules.length === 0 ? (
                      <p className="text-sm text-secondary italic">No rules defined.</p>
                    ) : (
                      doRules.map((item, idx) => <RuleCard key={idx} item={item} />)
                    )}
                  </AccordionContent>
                </AccordionPrimitive.AccordionItem>
              </Accordion>
              <Accordion type="single" collapsible>
                <AccordionPrimitive.AccordionItem
                  value="dont"
                  className={cn(
                    'rounded-sm border-base-border',
                    'not-data-[state=open]:hover:border-base-border-hover'
                  )}
                >
                  <AccordionTrigger hasIcon={false}>
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-2 text-sm font-bold tracking-wide text-attention-red uppercase">
                        <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Must Avoid (Don't)
                      </div>
                      <Icon icon="caret-down" size={9} variant="black" className="group-data-[state=open]:rotate-180" />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="p-2">
                    {dontRules.length === 0 ? (
                      <p className="text-sm text-secondary italic">No rules defined.</p>
                    ) : (
                      dontRules.map((item, idx) => <RuleCard key={idx} item={item} />)
                    )}
                  </AccordionContent>
                </AccordionPrimitive.AccordionItem>
              </Accordion>
            </>
          );
        })()}
      </div>
    );
  }

  return null;
};

const RuleCard = ({ item }: { item: TypedVideoAnalyzerRuleBreakdown & { ruleText: string; ruleType?: RuleType } }) => (
  <div
    className={`rounded-lg border p-3 ${
      item.status === 'PASS'
        ? 'border-attention-green bg-attention-green/10'
        : item.status === 'FAIL'
          ? 'border-attention-red bg-attention-red/10'
          : 'border-attention-yellow bg-attention-yellow/10'
    }`}
  >
    <div className="flex items-start gap-3">
      <div
        className={`mt-0.5 flex size-6 flex-shrink-0 items-center justify-center rounded-full border ${
          item.status === 'PASS'
            ? 'border-attention-green bg-attention-green/20 text-attention-green'
            : item.status === 'FAIL'
              ? 'border-attention-red bg-attention-red/10 text-attention-red'
              : 'border-attention-yellow bg-attention-yellow/10 text-attention-yellow'
        }`}
      >
        {item.status === 'PASS' ? (
          <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
          </svg>
        ) : item.status === 'FAIL' ? (
          <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <span className="text-xxs font-bold">?</span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-start justify-between gap-2">
          <h4 className="text-sm leading-tight font-medium text-primary">{item.ruleText}</h4>
          <span
            className={`rounded border px-1.5 py-0.5 text-xxs font-bold whitespace-nowrap ${
              item.status === 'PASS'
                ? 'border-attention-green bg-attention-green/10 text-attention-green'
                : item.status === 'FAIL'
                  ? 'border-attention-red bg-attention-red/10 text-attention-red'
                  : 'border-attention-yellow bg-attention-yellow/10 text-attention-yellow'
            }`}
          >
            {item.status}
          </span>
        </div>
        <p className="mb-2 text-xs leading-snug text-tertiary">{item.explanation}</p>
        <div className="flex gap-2 text-xxs text-tertiary">
          {item.timestampStart && (
            <span>
              @{item.timestampStart}
              {item.timestampEnd ? `-${item.timestampEnd}` : ''}
            </span>
          )}
          <span>Conf: {item.confidence}%</span>
        </div>
      </div>
    </div>
  </div>
);
